import { useEffect, useState } from "react";
import { api, formatErr } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import { RefreshCw, FileText } from "lucide-react";

const ENTITIES = ["", "user", "project", "task", "procurement", "approval", "payment", "attachment", "settings", "cleanup"];

export default function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [entity, setEntity] = useState("");
  const [event, setEvent] = useState("");
  const [days, setDays] = useState(7);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const params = { days, limit: 200 };
      if (entity) params.entity = entity;
      if (event) params.event = event;
      const [logsRes, statsRes] = await Promise.all([
        api.get("/admin/audit-logs", { params }),
        api.get("/admin/audit-logs/stats", { params: { days } }),
      ]);
      setLogs(logsRes.data);
      setStats(statsRes.data);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Failed to load audit log");
    } finally { setBusy(false); }
  };

  useEffect(() => { load(); }, [entity, event, days]);

  return (
    <div className="space-y-4" data-testid="audit-logs-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Compliance</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Audit Logs</h1>
          <p className="text-xs text-muted-foreground mt-1">Sensitive actions across the system. Retention: 12 months rolling.</p>
        </div>
        <Button variant="outline" onClick={load} disabled={busy}>
          <RefreshCw className={`h-4 w-4 mr-2 ${busy ? "animate-spin" : ""}`} />Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card className="p-4 bg-card border-border">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Entity</label>
            <Select value={entity || "all"} onValueChange={(v) => setEntity(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="audit-entity"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {ENTITIES.filter(Boolean).map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Event</label>
            <Input placeholder="e.g. payment.create" value={event} onChange={(e) => setEvent(e.target.value)} data-testid="audit-event" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Days</label>
            <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {[1, 7, 30, 90, 180, 365].map((d) => <SelectItem key={d} value={String(d)}>Last {d} day{d > 1 ? "s" : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {/* Stats */}
      {stats && (
        <Card className="p-4 bg-card border-border">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total events</div>
              <div className="font-mono text-3xl text-primary">{stats.total}</div>
            </div>
            <div className="text-xs text-muted-foreground">last {stats.days} day(s)</div>
          </div>
          <div className="flex flex-wrap gap-2">
            {stats.by_event.map((r) => (
              <button key={r.event} onClick={() => setEvent(r.event)}
                      className="px-2 py-1 rounded border border-border bg-secondary/40 text-xs hover:border-primary transition-colors">
                <span className="font-mono">{r.event}</span> <span className="text-primary">×{r.count}</span>
              </button>
            ))}
          </div>
        </Card>
      )}

      {/* Log rows */}
      <Card className="bg-card border-border overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead className="w-[180px]">When</TableHead>
            <TableHead>Event</TableHead>
            <TableHead>Actor</TableHead>
            <TableHead>Entity</TableHead>
            <TableHead>Details</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {logs.map((l) => (
              <TableRow key={l.id} data-testid={`audit-row-${l.id}`}>
                <TableCell className="font-mono text-[11px] text-muted-foreground">
                  {l.at_iso ? new Date(l.at_iso).toLocaleString("en-IN") : "—"}
                </TableCell>
                <TableCell className="font-mono text-xs">{l.event}</TableCell>
                <TableCell className="text-xs">
                  {l.actor_email || <span className="text-muted-foreground">system/anon</span>}
                  {l.actor_role && <span className="text-[10px] uppercase text-muted-foreground ml-1">({l.actor_role})</span>}
                </TableCell>
                <TableCell className="text-xs">
                  <span className="capitalize">{l.entity}</span>
                  {l.entity_id && <div className="text-[10px] font-mono text-muted-foreground truncate max-w-[160px]">{l.entity_id}</div>}
                </TableCell>
                <TableCell className="text-[11px] text-muted-foreground">
                  {l.meta && Object.keys(l.meta).length > 0 ? (
                    <details>
                      <summary className="cursor-pointer inline-flex items-center gap-1"><FileText className="h-3 w-3" />meta</summary>
                      <pre className="mt-1 text-[10px] whitespace-pre-wrap break-all">{JSON.stringify(l.meta, null, 2)}</pre>
                    </details>
                  ) : "—"}
                </TableCell>
              </TableRow>
            ))}
            {logs.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No events for the current filters.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
